import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Save, Plus } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useAppDispatch } from 'store/hooks';
import { showLoader, hideLoader } from 'store/uiSlice';
import { siteWheelService, SiteWheelPrize } from 'components/api/siteWheel';
import SortablePrizeRow from './SortablePrizeRow';
import FreeSpinGameSelectModal from './FreeSpinGameSelectModal';
import styles from './WheelPrizeSettings.module.scss';
import { Alert } from 'components/alert/alert';

interface Props {
  siteId: string | number;
}

const WheelPrizeSettings: React.FC<Props> = ({ siteId }) => {
  const dispatch = useAppDispatch();
  const [prizes, setPrizes] = useState<SiteWheelPrize[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [statusAlert, setStatusAlert] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const [gameSelectModal, setGameSelectModal] = useState<{ open: boolean; prizeId: number | null }>({
    open: false,
    prizeId: null,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (statusAlert) {
      const timer = setTimeout(() => setStatusAlert(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [statusAlert]);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await siteWheelService.getSettings(siteId);
      // Sort prizes by sortOrder initially
      const sortedPrizes = (res.data?.prizes || []).sort((a, b) => a.sortOrder - b.sortOrder);
      setPrizes(sortedPrizes);
      setHasChanges(false);
    } catch (err: any) {
      setStatusAlert({ message: err.message || 'Failed to fetch settings', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = prizes.findIndex((p) => p.id === active.id);
      const newIndex = prizes.findIndex((p) => p.id === over.id);
      const newPrizes = arrayMove(prizes, oldIndex, newIndex);
      setPrizes(newPrizes);
      setHasChanges(true);
    }
  };

  const handleUpdatePrize = (id: number, payload: Partial<SiteWheelPrize>) => {
    setPrizes((prev) => prev.map((p) => (p.id === id ? { ...p, ...payload } : p)));
    setHasChanges(true);
  };

  const handleDeletePrize = (id: number) => {
    setPrizes((prev) => prev.filter((p) => p.id !== id));
    setHasChanges(true);
  };

  const handleOpenGameSelect = (prizeId: number) => {
    setGameSelectModal({ open: true, prizeId });
  };

  const handleGameSelectConfirm = (gameId: number, betLevel: number) => {
    if (gameSelectModal.prizeId !== null) {
      handleUpdatePrize(gameSelectModal.prizeId, {
        freeSpinGameId: gameId,
        freeSpinBetLevel: betLevel,
      });
    }
    setGameSelectModal({ open: false, prizeId: null });
  };

  const handleSave = async () => {
    dispatch(showLoader({ text: 'Saving prizes...' }));
    try {
      // Re-assign sortOrder based on current index
      const updatedPrizes = prizes.map((p, index) => ({ ...p, sortOrder: index }));
      await siteWheelService.updatePrizes(siteId, updatedPrizes);
      setPrizes(updatedPrizes);
      setHasChanges(false);
      setStatusAlert({ message: 'Saved successfully', type: 'success' });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to save';
      setStatusAlert({ message: errorMessage, type: 'error' });
    } finally {
      dispatch(hideLoader());
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress sx={{ color: 'var(--accent-blue)' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {statusAlert && <Alert message={statusAlert.message} type={statusAlert.type} />}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button
          variant="contained"
          disabled={!hasChanges}
          startIcon={<Save size={18} />}
          onClick={handleSave}
          className={styles.saveBtn}
          sx={{ mt: 2 }}
        >
          Save
        </Button>
      </Box>

      <Box className={styles.tableWrapper}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table className={styles.prizesTable}>
            <thead>
              <tr>
                <th></th>
                <th>Prize Type</th>
                <th>Configuration</th>
                <th>Probability</th>
                <th>Auto Label</th>
              </tr>
            </thead>
            <tbody>
              <SortableContext
                items={prizes.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                {prizes.map((prize) => (
                  <SortablePrizeRow
                    key={prize.id}
                    prize={prize}
                    onUpdate={handleUpdatePrize}
                    onDelete={handleDeletePrize}
                    onOpenGameSelect={handleOpenGameSelect}
                  />
                ))}
              </SortableContext>
            </tbody>
          </table>
        </DndContext>
      </Box>

      <FreeSpinGameSelectModal
        open={gameSelectModal.open}
        siteId={siteId}
        onClose={() => setGameSelectModal({ open: false, prizeId: null })}
        onSelect={handleGameSelectConfirm}
      />
    </Box>
  );
};

export default WheelPrizeSettings;
